import React, { useState } from 'react';

const PaginatedPayrollPreview = ({ payrollRecords = [], attendanceRecords = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState('payroll');

  // Ensure we have valid arrays
  const safePayrollRecords = Array.isArray(payrollRecords) ? payrollRecords : [];
  const safeAttendanceRecords = Array.isArray(attendanceRecords) ? attendanceRecords : [];

  // Calculate pagination for payroll records
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentPayrollRecords = safePayrollRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPayrollPages = Math.ceil(safePayrollRecords.length / recordsPerPage);

  // Calculate pagination for attendance records
  const currentAttendanceRecords = safeAttendanceRecords.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalAttendancePages = Math.ceil(safeAttendanceRecords.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); // Reset to first page when changing records per page
  };

  const PayrollTable = () => (
    <div className="overflow-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-100 sticky top-0">
          <tr>
            <th className="px-2 py-1 text-left">Employee</th>
            <th className="px-2 py-1 text-left">Company</th>
            <th className="px-2 py-1 text-left">Dept</th>
            <th className="px-2 py-1 text-right">PTO/HP</th>
            <th className="px-2 py-1 text-right">S/L</th>
            <th className="px-2 py-1 text-left">Lunch</th>
            <th className="px-2 py-1 text-right">Total Hours</th>
            <th className="px-2 py-1 text-right">Check Cap Hours</th>
            <th className="px-2 py-1 text-right">Check Hours</th>
            <th className="px-2 py-1 text-right">Weekday Cash Hours</th>
            <th className="px-2 py-1 text-right">Weekend Cash Hours</th>
            <th className="px-2 py-1 text-right">Check Rate</th>
            <th className="px-2 py-1 text-right">Weekday Cash Rate</th>
            <th className="px-2 py-1 text-right">Weekend Cash Rate</th>
            <th className="px-2 py-1 text-right">Check Pay</th>
            <th className="px-2 py-1 text-right">Weekday Cash Pay</th>
            <th className="px-2 py-1 text-right">Weekend Cash Pay</th>
            <th className="px-2 py-1 text-right">Total Cash Pay</th>
            <th className="px-2 py-1 text-right">Total Pay</th>
          </tr>
        </thead>
        <tbody>
          {currentPayrollRecords.map((record, idx) => (
            <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="px-2 py-1 font-medium">{record.employee_name}</td>
              <td className="px-2 py-1">{record.company}</td>
              <td className="px-2 py-1">{record.department}</td>
              <td className="px-2 py-1 text-right">{record.pto_hp.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">{record.sick_leave.toFixed(1)}</td>
              <td className="px-2 py-1">{record.lunch}</td>
              <td className="px-2 py-1 text-right font-medium">{record.total_hours.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">{record.check_cap_hours.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">{record.check_hours.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">{record.weekday_cash_hours.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">{record.weekend_cash_hours.toFixed(1)}</td>
              <td className="px-2 py-1 text-right">${record.check_rate.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.weekday_cash_rate.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.weekend_cash_rate.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.check_pay.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.weekday_cash_pay.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.weekend_cash_pay.toFixed(2)}</td>
              <td className="px-2 py-1 text-right">${record.total_cash_pay.toFixed(2)}</td>
              <td className="px-2 py-1 text-right font-bold">${record.total_pay.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const AttendanceTable = () => (
    <div className="overflow-auto border border-gray-200 rounded">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-100 sticky top-0">
          <tr>
            <th className="px-2 py-1 text-left">Employee</th>
            <th className="px-2 py-1 text-left">Company</th>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Day</th>
            <th className="px-2 py-1 text-right">Hours</th>
            <th className="px-2 py-1 text-left">Store Code</th>
            <th className="px-2 py-1 text-left">Store Name</th>
          </tr>
        </thead>
        <tbody>
          {currentAttendanceRecords.map((record, idx) => (
            <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="px-2 py-1">{record.employee_name}</td>
              <td className="px-2 py-1">{record.company}</td>
              <td className="px-2 py-1">{record.date}</td>
              <td className="px-2 py-1">{record.day_name}</td>
              <td className="px-2 py-1 text-right">{record.hours.toFixed(1)}</td>
              <td className="px-2 py-1">{record.store_code}</td>
              <td className="px-2 py-1">{record.store_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Reduced to fit better
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="mt-4 space-y-3">
        {/* Records per page selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Records per page:</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-700">
            Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
            {Math.min(currentPage * recordsPerPage, activeTab === 'payroll' ? safePayrollRecords.length : safeAttendanceRecords.length)} of{' '}
            {activeTab === 'payroll' ? safePayrollRecords.length : safeAttendanceRecords.length} records
          </div>
        </div>
        
        {/* Pagination controls */}
        <div className="flex items-center justify-center space-x-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            First
          </button>
          
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <span className="px-2 text-sm text-gray-500">...</span>
            </>
          )}
          
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => onPageChange(number)}
              className={`px-3 py-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                number === currentPage 
                  ? 'bg-blue-600 text-white border border-blue-600 hover:bg-blue-700' 
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {number}
            </button>
          ))}
          
          {endPage < totalPages && (
            <>
              <span className="px-2 text-sm text-gray-500">...</span>
            </>
          )}
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            Next
          </button>
          
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Tab Navigation - Updated 2025-07-07 */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => {
            setActiveTab('payroll');
            setCurrentPage(1);
          }}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'payroll'
              ? 'border-blue-500 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Payroll Records ({safePayrollRecords.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('attendance');
            setCurrentPage(1);
          }}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'attendance'
              ? 'border-blue-500 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Attendance Records ({safeAttendanceRecords.length})
        </button>
      </div>

      {/* Table Content */}
      <div className="max-h-96 overflow-auto">
        {activeTab === 'payroll' ? <PayrollTable /> : <AttendanceTable />}
      </div>

      {/* Pagination */}
      <PaginationControls
        currentPage={currentPage}
        totalPages={activeTab === 'payroll' ? totalPayrollPages : totalAttendancePages}
        onPageChange={paginate}
      />
    </div>
  );
};

export default PaginatedPayrollPreview;